/**
 * MyReferrals — iter-185 page, expanded in iter-220.2.
 *
 * Visible only when the campaign has at least one active rule (the
 * sidebar link is also conditional). Surfaces:
 *
 *   1. Hero stat: validated invites + share link copy + regenerate.
 *   2. Reward TIER LADDER — visual progress across every active rule
 *      with checkmarks for unlocked tiers, a moving cursor on the
 *      next-up tier, and dimmed future tiers.
 *   3. Invitees table — privacy-safe (first-name only) list of who
 *      signed up via this referrer with a status pill
 *      (Registrado / Onboarding / Pago).
 *   4. Conversion metrics — total invitees + onboarded + paid ratio.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Copy, Gift, Trophy, Users, Calendar as CalendarIcon, RefreshCw,
  CheckCircle2, Circle, TrendingUp, UserPlus,
} from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { toast } from "sonner";

const StatusPill = ({ status, lang }) => {
  const cfg = {
    signup: {
      cls: "bg-slate-100 text-slate-700 border-slate-200",
      label: tx(lang, "Signed up", "Registrado", "Cadastrado"),
    },
    onboarded: {
      cls: "bg-sky-50 text-sky-700 border-sky-200",
      label: tx(lang, "Onboarded", "Onboarding", "Onboarding"),
    },
    paid: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      label: tx(lang, "Paid", "Pago", "Pago"),
    },
  }[status] || { cls: "bg-slate-50 text-slate-500 border-slate-200", label: status };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cfg.cls}`}
      data-testid={`invitee-status-${status}`}
    >
      {cfg.label}
    </span>
  );
};

function RewardLadder({ rules, validated, lang }) {
  // Sorted by threshold ASC (server already does this). Each rule is
  // either unlocked, current (next-up), or future.
  if (!rules?.length) return null;
  const nextIdx = rules.findIndex((r) => !r.unlocked && validated < r.threshold);
  return (
    <ol
      className="relative border-l-2 border-dashed border-slate-200 ml-3 space-y-5"
      data-testid="referrals-ladder"
    >
      {rules.map((r, idx) => {
        const isUnlocked = r.unlocked;
        const isCurrent = !isUnlocked && idx === nextIdx;
        const progress = isCurrent
          ? Math.min(100, Math.round((validated / r.threshold) * 100))
          : isUnlocked ? 100 : 0;
        const Icon = isUnlocked ? CheckCircle2 : Circle;
        const ringCls = isUnlocked
          ? "bg-emerald-100 text-emerald-700 ring-2 ring-emerald-200"
          : isCurrent
          ? "bg-sky-100 text-sky-700 ring-2 ring-sky-300 animate-pulse"
          : "bg-slate-100 text-slate-400 ring-2 ring-slate-200";
        return (
          <li
            key={r.id}
            className="ml-6"
            data-testid={`ladder-tier-${r.id}`}
          >
            <span
              className={`absolute -left-[18px] flex h-9 w-9 items-center justify-center rounded-full ${ringCls}`}
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="flex items-baseline justify-between gap-3">
              <h3 className={`text-sm font-semibold ${isUnlocked ? "text-emerald-700" : isCurrent ? "text-sky-700" : "text-slate-500"}`}>
                {r.name}
              </h3>
              <span className="text-[11px] font-bold tabular-nums text-slate-500">
                {Math.min(validated, r.threshold)} / {r.threshold}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {tx(lang,
                `Reward: ${r.reward_kind}`,
                `Premio: ${r.reward_kind === "plan" ? "Plan" : "Pack"}`,
                `Prêmio: ${r.reward_kind}`)}
              {r.reward_duration_months ? ` · ${r.reward_duration_months} ${tx(lang, "months", "meses", "meses")}` : ""}
              {r.reward_units ? ` · ${r.reward_units} ${tx(lang, "units", "unidades", "unidades")}` : ""}
            </p>
            <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${isUnlocked ? "bg-emerald-500" : isCurrent ? "bg-sky-500" : "bg-slate-300"}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function MyReferrals() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["referrals-me"],
    queryFn: async () => (await api.get("/referrals/me")).data,
    staleTime: 60_000,
  });
  const { data: ref } = useQuery({
    queryKey: ["me-referral"],
    queryFn: async () => (await api.get("/me/referral")).data,
    staleTime: 24 * 60 * 60 * 1000,
  });
  const { data: invitees } = useQuery({
    queryKey: ["referrals-invitees"],
    queryFn: async () => (await api.get("/referrals/me/invitees")).data,
    staleTime: 30_000,
    enabled: !!data?.any_active,
  });

  const regenerateMut = useMutation({
    mutationFn: async () => (await api.post("/referrals/me/regenerate-code")).data,
    onSuccess: (resp) => {
      qc.setQueryData(["me-referral"], resp);
      toast.success(tx(lang,
        "New invite link generated.",
        "Nuevo enlace de invitación generado.",
        "Novo link de convite gerado."));
    },
    onError: () => toast.error(tx(lang,
      "Could not regenerate the link.",
      "No se pudo regenerar el enlace.",
      "Não foi possível gerar o link.")),
  });

  const copyShareUrl = async () => {
    if (!ref?.share_url) return;
    try {
      await navigator.clipboard.writeText(ref.share_url);
      toast.success(tx(lang, "Link copied", "Enlace copiado", "Link copiado"));
    } catch { /* ignore */ }
  };

  if (isLoading) {
    return (
      <Layout>
        <PageHeader title={tx(lang, "Referrals", "Referidos", "Indicações")} />
      </Layout>
    );
  }

  if (!data?.any_active) {
    return (
      <Layout>
        <PageHeader title={tx(lang, "Referrals", "Referidos", "Indicações")} />
        <Card className="p-6 text-sm text-slate-600">
          {tx(lang,
            "There is no active referral campaign at the moment. Check back soon.",
            "No hay campaña de referidos activa en este momento. Vuelve pronto.",
            "Não há campanha de indicações ativa no momento. Volte em breve.")}
        </Card>
      </Layout>
    );
  }

  const validated = data.validated_count;
  const next = data.next_rule;
  const ends = data.campaign_ends_at;
  const totals = invitees?.totals || { signup: 0, onboarded: 0, paid: 0 };
  const totalInvited = totals.signup + totals.onboarded + totals.paid;
  // Conversion = (onboarded + paid) / total invitees.
  const conversionPct = totalInvited > 0
    ? Math.round(((totals.onboarded + totals.paid) / totalInvited) * 100)
    : 0;

  return (
    <Layout>
      <PageHeader
        eyebrow={tx(lang, "Lumfi rewards", "Recompensas Lumfi", "Recompensas Lumfi")}
        title={tx(lang, "Referrals", "Referidos", "Indicações")}
        subtitle={tx(lang,
          "Invite friends and unlock rewards.",
          "Invita amigos y desbloquea recompensas.",
          "Convide amigos e desbloqueie recompensas.")}
        testid="referrals-page-header"
      />

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <Card className="p-5 md:col-span-1" data-testid="referrals-progress-card">
          <div className="flex items-center gap-2 text-slate-700 text-sm font-semibold mb-2">
            <Users className="h-4 w-4" />
            {tx(lang, "Validated invites", "Invitaciones validadas", "Convites validados")}
          </div>
          <div className="text-4xl font-bold text-emerald-600" data-testid="referrals-validated-count">
            {validated}
          </div>
          {next ? (
            <div className="text-xs text-slate-500 mt-3">
              {tx(lang,
                `${next.threshold - validated} more to unlock “${next.name}”`,
                `Te faltan ${next.threshold - validated} para desbloquear «${next.name}»`,
                `Faltam ${next.threshold - validated} para desbloquear "${next.name}"`)}
            </div>
          ) : (
            <div className="text-xs text-emerald-700 mt-3 font-medium">
              {tx(lang,
                "You've unlocked every active reward.",
                "Desbloqueaste todas las recompensas activas.",
                "Você desbloqueou todas as recompensas ativas.")}
            </div>
          )}
        </Card>

        <Card className="p-5 md:col-span-1" data-testid="referrals-metrics-card">
          <div className="flex items-center gap-2 text-slate-700 text-sm font-semibold mb-2">
            <TrendingUp className="h-4 w-4" />
            {tx(lang, "Conversion", "Conversión", "Conversão")}
          </div>
          <div className="text-4xl font-bold text-sky-600 tabular-nums" data-testid="referrals-conversion">
            {conversionPct}%
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-[11px] text-slate-600">
            <div data-testid="metric-signup" className="flex flex-col">
              <span className="font-bold tabular-nums">{totals.signup}</span>
              <span className="text-slate-400">{tx(lang, "Signed up", "Registrados", "Cadastrados")}</span>
            </div>
            <div data-testid="metric-onboarded" className="flex flex-col">
              <span className="font-bold tabular-nums">{totals.onboarded}</span>
              <span className="text-slate-400">{tx(lang, "Onboarded", "Onboarding", "Onboarding")}</span>
            </div>
            <div data-testid="metric-paid" className="flex flex-col">
              <span className="font-bold tabular-nums">{totals.paid}</span>
              <span className="text-slate-400">{tx(lang, "Paid", "Pagos", "Pagos")}</span>
            </div>
          </div>
        </Card>

        <Card className="p-5 md:col-span-1" data-testid="referrals-share-card">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-slate-700 text-sm font-semibold">
              <Gift className="h-4 w-4" />
              {tx(lang, "Your invite link", "Tu link de invitación", "Seu link de convite")}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => regenerateMut.mutate()}
              disabled={regenerateMut.isPending}
              data-testid="referrals-regenerate-btn"
              title={tx(lang, "Generate a new code", "Regenerar el código", "Regenerar o código")}
              className="text-[11px] gap-1 h-7 px-2"
            >
              <RefreshCw className={`h-3 w-3 ${regenerateMut.isPending ? "animate-spin" : ""}`} />
              {tx(lang, "Regenerate", "Regenerar", "Regenerar")}
            </Button>
          </div>
          <div
            className="bg-slate-50 border rounded-lg p-3 text-xs font-mono text-slate-700 break-all"
            data-testid="referrals-share-url"
          >
            {ref?.share_url || "—"}
          </div>
          <Button
            className="mt-3 w-full"
            onClick={copyShareUrl}
            data-testid="referrals-copy-link-btn"
          >
            <Copy className="h-4 w-4 mr-2" />
            {tx(lang, "Copy link", "Copiar enlace", "Copiar link")}
          </Button>
          {ends && (
            <div className="flex items-center gap-1 text-[11px] text-amber-700 mt-3">
              <CalendarIcon className="h-3 w-3" />
              {tx(lang,
                `Campaign ends on ${ends.slice(0, 10)}`,
                `La campaña termina el ${ends.slice(0, 10)}`,
                `A campanha termina em ${ends.slice(0, 10)}`)}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5 mb-6" data-testid="referrals-ladder-card">
        <div className="flex items-center gap-2 text-slate-700 text-sm font-semibold mb-4">
          <Trophy className="h-4 w-4" />
          {tx(lang, "Reward tiers", "Niveles de recompensa", "Níveis de recompensa")}
        </div>
        <RewardLadder rules={data.rules || []} validated={validated} lang={lang} />
      </Card>

      <Card className="p-5" data-testid="referrals-invitees-card">
        <div className="flex items-center gap-2 text-slate-700 text-sm font-semibold mb-3">
          <UserPlus className="h-4 w-4" />
          {tx(lang, "Your invitees", "Tus invitados", "Seus convidados")}
          <span className="ml-auto text-xs text-slate-400 font-normal">
            {totalInvited} {tx(lang, "total", "total", "total")}
          </span>
        </div>
        {totalInvited === 0 ? (
          <div className="text-sm text-slate-500" data-testid="invitees-empty">
            {tx(lang,
              "Nobody has signed up with your link yet. Share it to start unlocking rewards.",
              "Nadie se ha registrado con tu link todavía. Compártelo para empezar a desbloquear recompensas.",
              "Ninguém se cadastrou com seu link ainda. Compartilhe para começar a desbloquear recompensas.")}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="invitees-list">
            {(invitees?.invitees || []).map((inv) => (
              <li
                key={inv.id}
                className="py-2.5 flex items-center justify-between gap-2"
                data-testid={`invitee-row-${inv.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center text-xs font-bold">
                    {(inv.name && inv.name.charAt(0).toUpperCase()) || "?"}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {inv.name || tx(lang, "Friend", "Amigo", "Amigo")}
                    </div>
                    {inv.joined_at && (
                      <div className="text-[11px] text-slate-500">
                        {tx(lang, "Joined", "Se unió", "Entrou")}{" "}
                        {new Date(inv.joined_at).toLocaleDateString(isEs ? "es" : "en", {
                          day: "numeric", month: "short", year: "numeric",
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <StatusPill status={inv.status} lang={lang} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Layout>
  );
}
